// ==========================================
// Palette (Primitive Colors)
// ==========================================
const palette = {
  // Base
  white: "#FFFFFF",
  black: "#000000",

  // Gray
  gray_50: "#F4F5F6",
  gray_100: "#E8E9EA",
  gray_150: "#DDDEDF",
  gray_200: "#CBCDCF",
  gray_250: "#BABDBF",
  gray_300: "#989CA0",
  gray_400: "#868B90",
  gray_500: "#767A7F",
  gray_600: "#666A6E",
  gray_700: "#46494C",
  gray_800: "#36383A",
  gray_900: "#171819",

  // Yellow
  yellow_50: "#FFFCF6",
  yellow_100: "#FFF1D7",
  yellow_150: "#FFE6B9",
  yellow_200: "#FFDB9C",
  yellow_250: "#FFD07E",
  yellow_300: "#FFC560",
  yellow_400: "#FFBB42",
  yellow_500: "#E6A73B",
  yellow_600: "#CB9232",
  yellow_700: "#976A21",
  yellow_800: "#624211",
  yellow_900: "#2E1A00",

  // Orange
  orange_50: "#FFFBF5",
  orange_100: "#FFEADC",
  orange_150: "#FFD3B9",
  orange_200: "#FFBF9B",
  orange_250: "#FFAC7E",
  orange_300: "#FF9860",
  orange_400: "#FF8442",
  orange_500: "#FF7225",
  orange_600: "#E45E15",
  orange_700: "#943F12",
  orange_800: "#5F2709",
  orange_900: "#2A0F00",

  // Red
  red_50: "#FFFAFA",
  red_100: "#FFEDED",
  red_150: "#FFD3D3",
  red_200: "#FFABAB",
  red_250: "#FF9191",
  red_300: "#FF7777",
  red_400: "#FF5C5C",
  red_500: "#FF4242",
  red_600: "#CB3232",
  red_700: "#972121",
  red_800: "#621010",
  red_900: "#2E0000",

  // Blue
  blue_50: "#F6FAFF",
  blue_100: "#E5F1FF",
  blue_150: "#C6DDFF",
  blue_200: "#AFD0FF",
  blue_250: "#8CBCFF",
  blue_300: "#69A7FF",
  blue_400: "#4693FF",
  blue_500: "#237EFF",
  blue_600: "#006AFF",
  blue_700: "#004DB8",
  blue_800: "#002F70",
  blue_900: "#00122A",

  // Green
  green_50: "#F6FFF2",
  green_100: "#DFFBDE",
  green_150: "#C8F6D0",
  green_200: "#99EDB1",
  green_250: "#6BE591",
  green_300: "#3CDC72",
  green_400: "#0CCF50",
  green_500: "#09B646",
  green_600: "#069B3A",
  green_700: "#04722B",
  green_800: "#02481B",
  green_900: "#001F0B",

  // Warmgray
  warmgray_50: "#F6F4F4",
  warmgray_100: "#EAE5E5",
  warmgray_150: "#DDD7D7",
  warmgray_200: "#D1CAC9",
  warmgray_250: "#B9B3B2",
  warmgray_300: "#A29C9C",
  warmgray_400: "#8B8685",
  warmgray_500: "#74706F",
  warmgray_600: "#5D5959",
  warmgray_700: "#464343",
  warmgray_800: "#2F2C2C",
  warmgray_900: "#191717",
} as const;

// ==========================================
// Semantic (Purpose-based Colors)
// ==========================================
const semantic = {
  // Primary & Sub
  primary_orange: palette.orange_500,
  sub_warmgray: palette.warmgray_200,

  // Background
  background_normal: palette.white,
  background_accent_1: palette.gray_50,
  background_accent_2: palette.gray_100,

  // Label (Text)
  label_normal: palette.gray_900,
  label_alternative_1: palette.gray_700,
  label_alternative_2: palette.gray_800,
  label_assistive_1: palette.gray_500,
  label_assistive_2: palette.gray_600,
  label_muted_1: palette.gray_200,
  label_muted_2: palette.gray_300,

  // Fill
  fill_normal: palette.gray_50,
  fill_accent: palette.gray_100,

  // Line (Border)
  line_alternative: palette.gray_50,
  line_normal: palette.gray_100,
  line_accent: palette.gray_150,

  // Status
  status_positive: palette.green_400,
  status_cautionary: palette.yellow_400,
  status_negative: palette.red_500,

  // Accent
  accent_blue: palette.blue_500,
  accent_yellow: palette.yellow_600,
} as const;

export const baseColors = {
  ...palette,
  ...semantic,
} as const;

export type ColorToken = keyof typeof baseColors;
